import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spin } from 'antd';
import useCurrentUser from 'hooks/useCurrentUser';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import useEhrConnections from 'hooks/useEhrConnections';
import ProviderStore from 'store/Provider';
import BillingInfoModal from 'pages/InsuranceBillingOnboarding/components/BillingInfoModal';
import type { BillingStepKey } from 'pages/InsuranceBillingOnboarding/steps';
import { fetchOnboardingVo } from 'pages/InsuranceBillingOnboarding/billingReviewData';
import TodoListModal from 'pages/dashboard/ProviderHomePage/components/TodoList/TodoListModal';
import { getBillingBoardSummary, getBillingClaimKpi, saveBillingClaimScope } from 'api/insuranceBilling';
import type {
    BoardSummaryVo,
    InsuranceBillingOnboardingVo,
    KpiQuery,
    TBillingApiScope,
    TClaimViewScope,
} from 'types/insuranceBilling';
import { getKey } from 'utils/localstore';
import { isBillingOwnerOrAdmin } from '../access';
import Landing from '../Landing';
import KpiCards, { DEFAULT_KPI_PERIOD, KPI_PERIOD_ALL_TIME } from './KpiCards';
import FeedbackModal from './FeedbackModal';
import WaitingCard from './WaitingCard';
import type { WaitingEhrState } from './WaitingCard';
import TagClientsBanner from './TagClientsBanner';
import ClaimsTab from './ClaimsTab';
import EraTab from './EraTab';
import ClaimDrawer from './ClaimDrawer';
import VisitDrawer from './VisitDrawer';
import BillMorePatientsModal from './BillMorePatientsModal';
import ReportDrawer from './ReportDrawer';
import ToolbarActions from './ToolbarActions';
import ReportButton from './ReportButton';
import { utcDay } from './statusConfig';
import { EhrConnectModal } from 'components/EhrConnect';
import s from './s.module.less';

type TabKey = 'claims' | 'era';

// beta 期间 claim / payment 的问题走跟计费 PM 约时间（原型 PM_CALENDLY）
const BILLING_TEAM_CALENDLY = 'https://calendly.com/d/dz59-5yf-pcn/guorui-tia-meeting-30-minutes?month=2026-09';

// waiting 态 wow panel 的「View all results」点过没有：点过之后面板不再出现（原型 wowSeen）。
// TODO(backend): 先存 localStorage（按登录用户隔离），换设备/清缓存会再看到一次面板；
// 待与后端确认是否提供服务端 flag，有的话换成接口读写
const WOW_SEEN_KEY = 'insurance_billing_wow_seen';
const readWowSeen = (): boolean => {
    try {
        return localStorage.getItem(getKey(WOW_SEEN_KEY, true)) === '1';
    } catch (e) {
        return false;
    }
};
const writeWowSeen = () => {
    try {
        localStorage.setItem(getKey(WOW_SEEN_KEY, true), '1');
    } catch (e) {
        // 存不下（隐私模式等）只是下次还会看到面板，不影响本次
    }
};

const InsuranceBillingDashboard = () => {
    const [currentUser] = useCurrentUser();
    const [getUser] = ProviderStore.useStore();
    // gate（../index.tsx）进入 dashboard 前已拉过 /status，hook 走全局缓存不会二次请求；
    // waiting 态的 4 个阻塞布尔（service 配齐 / note 未提交，本人 + 团队口径）也在这上面
    const [billingStatus, , refetchBillingStatus] = useInsuranceBillingStatus();
    const isGroup = !!currentUser?.groupPractice;
    // owner / 代持 owner 的 admin staff（可编辑）；否则团队 provider（只读，只看自己的 claims）。
    // 按 currentUser 判定而非 /status 的 canBind：canBind 在 OPS 代登录下被后端有意置 false
    //（ops 不得代 provider 发起绑定），拿它当 owner 用会让 ops preview 的 group owner 只剩 My billing
    const canManage = isBillingOwnerOrAdmin(currentUser); //https://applink.larksuite.com/client/todo/detail?guid=adfc8581-38c2-4916-b769-75be5ac1b6a7&suite_entity_num=t103136
    const selfProviderId: number | undefined = getUser('data')?.id;

    // group 专属子视图：practice = 全诊所 / my = 自己的 claims；solo 恒为 mine
    const [view, setView] = useState<'practice' | 'my'>('practice');
    const [tab, setTab] = useState<TabKey>('claims');
    // kpiAllTotal 是 /claims/kpi 不带日期的全量 total，只用来判定 waiting 态（时间筛选不能
    // 把「最近一个月没有新就诊」的老用户误判成还没开始）；卡片上展示的 KPI 来自 summary.kpi（按所选期间）
    const [kpiAllTotal, setKpiAllTotal] = useState<number | null>(null);
    const [kpiLoading, setKpiLoading] = useState(true);
    const [kpiPeriod, setKpiPeriod] = useState(DEFAULT_KPI_PERIOD);
    const [wowSeen, setWowSeen] = useState(readWowSeen);
    // board summary：tab 计数、NEEDS ATTENTION、筛选选项、首批面板统计。随 scope 与 KPI 期间变化（见 boardRange）
    const [summary, setSummary] = useState<BoardSummaryVo | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    // 变化即让 summary 与表格重拉（打开 visit 时发现已出 claim 等）
    const [boardVersion, setBoardVersion] = useState(0);
    const [openClaimId, setOpenClaimId] = useState<number | null>(null);
    // 还没变成 claim 的 visit 抽屉（按 encounterId 拉详情）
    const [openVisitId, setOpenVisitId] = useState<number | null>(null);
    // waiting 态 wow panel 的「Bill for more patients」：把 onboarding Step 6 的范围问题
    // 搬到弹窗里，起步范围定窄了的 provider 不必绕回 Settings 才能放宽
    const [billMoreOpen, setBillMoreOpen] = useState(false);
    // Settings 复用 onboarding 的 BillingInfoModal；Add provider 直接定位 rendering tab
    const [settingsTab, setSettingsTab] = useState<BillingStepKey | null>(null);
    // Weekly report drawer：非 null 即打开，值为周一 YYYY-MM-DD
    const [reportWeekStart, setReportWeekStart] = useState<string | null>(null);
    const [connectEhrOpen, setConnectEhrOpen] = useState(false);
    // 底部帮助板块的两个入口：How it works → 只读 landing（带 Back）；Share feedback → 建议弹窗
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    // Review pending notes → Home 的 To-do list（notes 在那里签，不重建队列）
    const [todoOpen, setTodoOpen] = useState(false);

    // waiting 变体与 Tag clients banner 依赖的 EHR 侧状态：claim-scope 设置 + 连接列表（isCustom 判定）
    // undefined = 拉取中，null = 拉取失败（按无 claimSettings 兜底）
    const [vo, setVo] = useState<InsuranceBillingOnboardingVo | null | undefined>(undefined);
    const [connections, connectionsLoading] = useEhrConnections();

    // 调用者是否在 rendering 名册（出诊）：/status 的两个 currentProvider* 字段为 null = 不出诊
    const viewerRenders =
        billingStatus?.currentProviderServiceTemplatePending != null ||
        billingStatus?.currentProviderTreatmentPlanPending != null;
    // Practice view / My billing 切换只对 group 的 owner / admin staff 开放；团队 provider 恒为 My billing。
    // 不出诊的 owner/admin 没有 My billing（个人视角无意义，/my 端点也认不出他，91038）→ 恒为 Practice view
    const showViewTabs = isGroup && canManage && viewerRenders;
    const scope: TClaimViewScope = isGroup && canManage && !(showViewTabs && view === 'my') ? 'practice' : 'mine';
    const showProviderCol = scope === 'practice';
    // 端点镜像：仅 group 的 My billing 走 /my/*；solo 与 group 的 Practice view 都走 practice 端点
    const apiScope: TBillingApiScope = isGroup && scope === 'mine' ? 'my' : 'practice';
    // My billing 视图只读（无 Add / Settings）；group 只在 Practice view 下可编辑，solo 恒可编辑
    const showToolbar = canManage && (!isGroup || scope === 'practice');
    // ERA 无法按 provider 拆分（金额是整张支票合计），只有 practice 级数据：
    // My billing 视图（含团队 provider 的唯一视图）不展示 ERA tab
    const showEraTab = apiScope === 'practice';

    // 已激活但还没有任何 claim（未过滤 KPI total = 0）→ waiting 态。
    // 例外：owner/admin 切到 My billing（只有 Practice view 有数据才切得进来）本人 0 条时
    // 显示空表格而非 waiting——waiting 变体讲的是"诊所还没跑起来"，对这个视角不成立，
    // 且 waiting 会隐藏 Practice/My 切换导致回不去。团队 provider（无切换）仍按本人判定。
    // 只认确定的 0：KPI 接口失败（null）判断不出就按「已有 claim」走表格态——表格 / 提醒卡 / ERA
    // 用的是 board、summary 等其他接口，不能因为这一个接口挂了就把出单中的诊所说成还没开始
    const waiting = !kpiLoading && kpiAllTotal === 0 && !(showViewTabs && view === 'my');
    // KPI 所选期间（date of service 落在「今天往前 N 个月」，UTC 日历日；All time 不带日期）。卡片 KPI 与下方列表
    // （board + summary）共用这一个窗口，切期间时列表跟着变；waiting 态（含 visitsOnly）不显示期间
    // 选择器，summary 还要喂首批面板，所以只在表格态带日期，否则按全量
    const kpiRange = useMemo((): KpiQuery => {
        if (kpiPeriod === KPI_PERIOD_ALL_TIME) return {};
        const today = utcDay();
        return {
            dosFrom: today.subtract(kpiPeriod, 'month').format('YYYY-MM-DD'),
            dosTo: today.format('YYYY-MM-DD'),
        };
    }, [kpiPeriod]);
    const boardRange = !kpiLoading && !waiting ? kpiRange : undefined;

    useEffect(() => {
        let cancelled = false;
        setKpiLoading(true);
        (async () => {
            const res: any = await getBillingClaimKpi(apiScope);
            if (cancelled) return;
            setKpiAllTotal(!res?.error ? (res?.data?.data?.total ?? null) : null);
            setKpiLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [apiScope]);

    // 等全量 KPI 判出 waiting / 表格态再拉：两种态的日期口径不同，提前拉会白拉一次。
    // 卡片 KPI 也在这里（summary.kpi，10-21 起与 tab 同口径），不再单独按期间调 /claims/kpi
    useEffect(() => {
        if (kpiLoading) return undefined;
        let cancelled = false;
        setSummaryLoading(true);
        (async () => {
            const res: any = await getBillingBoardSummary(boardRange || {}, apiScope);
            if (cancelled) return;
            setSummary(!res?.error ? (res?.data?.data ?? null) : null);
            setSummaryLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [apiScope, boardVersion, kpiLoading, boardRange]);

    // 切 Practice / My：关掉旧范围的详情，避免旧 scope 的数据留在抽屉里
    useEffect(() => {
        setOpenClaimId(null);
        setOpenVisitId(null);
        setSummary(null);
    }, [apiScope]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await fetchOnboardingVo();
            if (!cancelled) setVo(data);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Settings / Connect EHR 弹窗里改动（billingEhrs / claimScope / rendering providers）会影响
    // waiting 变体、Tag banner、Practice/My 切换：关弹窗时重拉 claimSettings 与 /status
    const refreshAfterSettings = () => {
        (async () => {
            const data = await fetchOnboardingVo();
            setVo(data);
        })();
        refetchBillingStatus();
    };

    // 从 dashboard 连上新 EHR（含登记 custom）：默认并入 claim settings 的 billingEhrs 保存
    // （其余 claim-scope 字段沿用服务端已存值），与 SubmittedPage / Settings 的 connect 行为一致
    const handleEhrConnected = async (ehrName: string) => {
        setConnectEhrOpen(false);
        const cs = vo?.claimSettings;
        const current = cs?.billingEhrs || [];
        if (!current.includes(ehrName)) {
            try {
                await saveBillingClaimScope({
                    billingEhrs: [...current, ehrName],
                    claimScope: cs?.claimScope ?? undefined,
                    claimStartMode: cs?.claimStartMode ?? undefined,
                    claimStartDate: cs?.claimStartDate ?? undefined,
                });
            } catch (e) {
                // 保存失败已由全局 toast 展示；连接本身已成功
            }
        }
        refreshAfterSettings();
    };

    // owner/admin 在 ERA tab 上切到 My billing 时，ERA tab 隐藏 → 回落 Claims
    useEffect(() => {
        if (!showEraTab && tab === 'era') setTab('claims');
    }, [showEraTab, tab]);

    // billingEhrs 里自建（isCustom）与受支持 EHR 混存，用连接记录区分；
    // Klarity EHR 判定沿用 EhrConnectFlow 的 name.includes('Klarity') 惯例
    const ehrState: WaitingEhrState = useMemo(() => {
        const customNames = new Set(connections.filter((c) => c.isCustom).map((c) => c.ehrSystem));
        const billingEhrs = vo?.claimSettings?.billingEhrs || [];
        const supported = billingEhrs.filter((n) => !customNames.has(n));
        return {
            supported,
            pending: billingEhrs.filter((n) => customNames.has(n)),
            klarityConnected: supported.some((n) => n.includes('Klarity')),
            others: supported.filter((n) => !n.includes('Klarity')),
        };
    }, [vo, connections]);
    const ehrReady = vo !== undefined && !connectionsLoading;

    // "You" pill：比对当前登录 provider 的 id（后端归属字段 providerId）
    const isSelfProvider = (providerId?: number | null): boolean =>
        providerId != null && selfProviderId != null && providerId === selfProviderId;

    // waiting 态点过 View all results：面板换成 claims 表格（原型 visitsOnly）。还没有任何 claim，
    // 所以 board 里只有 visit 行；没有 visit 时表格是空的，仍留在面板
    const visitsOnly = waiting && wowSeen && (summary?.visits.total ?? 0) > 0;
    const handleViewAll = () => {
        writeWowSeen();
        setWowSeen(true);
        window.scrollTo(0, 0);
    };

    // Tag clients banner 仅 waiting 态（原型 markClientsHtml）：Klarity 已连 + Step 6 选了
    // "Only tagged patients"（All patients 没东西可 tag）+ 调用者出诊（不出诊的人没有自己的 client 可 tag）
    const showTagBanner = ehrState.klarityConnected && vo?.claimSettings?.claimScope === 'tagged' && viewerRenders;

    const toolbarActions = showToolbar ? (
        <ToolbarActions
            isGroup={isGroup}
            onConnectEhr={() => setConnectEhrOpen(true)}
            onAddProvider={() => setSettingsTab('rendering-providers')}
            onOpenSettings={() => setSettingsTab('billing-provider')}
        />
    ) : null;

    // 页面动作挂在标题行右侧（10/05）：表格态只在 Claims tab（它们作用于 claims 表，ERA tab 上
    // 不该出现无效按钮），带 Report；waiting 态（含 View all results 之后）只有 Add / Settings
    let headerActions: React.ReactNode = null;
    if (!kpiLoading && waiting) headerActions = toolbarActions;
    if (!kpiLoading && !waiting && tab === 'claims') {
        headerActions = (
            <>
                {toolbarActions}
                <ReportButton onOpenReport={setReportWeekStart} />
            </>
        );
    }
    const kpiCards = (
        <KpiCards
            kpi={summary?.kpi ?? null}
            loading={kpiLoading || summaryLoading}
            period={kpiPeriod}
            onPeriodChange={setKpiPeriod}
            showPeriod={!kpiLoading && !waiting}
        />
    );
    // 打开 visit 时它已经出了 claim（出单竞态）：同一 scope 下切到 claim 抽屉，并刷新列表与计数
    const handleVisitClaimFound = (claimId: number) => {
        setOpenVisitId(null);
        setOpenClaimId(claimId);
        setBoardVersion((v) => v + 1);
    };
    const claimsTable = (
        <ClaimsTab
            apiScope={apiScope}
            showProviderCol={showProviderCol}
            isSelfProvider={isSelfProvider}
            summary={summary}
            dosFrom={boardRange?.dosFrom}
            dosTo={boardRange?.dosTo}
            refreshKey={boardVersion}
            onOpenClaim={setOpenClaimId}
            onOpenVisit={setOpenVisitId}
        />
    );

    // How it works：整页切到只读 landing（隐藏 Get started 等 CTA），Back 返回 dashboard
    if (howItWorksOpen) {
        return <Landing viewOnly onBack={() => setHowItWorksOpen(false)} />;
    }

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div>
                    <h1 className={s.title}>Insurance billing</h1>
                    <p className={s.sub}>Track and manage your insurance claims.</p>
                </div>
                {headerActions && <div className={s.headerActions}>{headerActions}</div>}
            </div>

            {/* waiting 态无数据可切视角，Practice/My 切换只在表格态出现（原型 render()） */}
            {showViewTabs && !kpiLoading && !waiting && (
                <div className={s.viewTabs}>
                    <button
                        type="button"
                        className={`${s.viewTab} ${view === 'practice' ? s.viewTabActive : ''}`}
                        onClick={() => setView('practice')}
                    >
                        Practice view
                    </button>
                    <button
                        type="button"
                        className={`${s.viewTab} ${view === 'my' ? s.viewTabActive : ''}`}
                        onClick={() => setView('my')}
                    >
                        My billing
                    </button>
                </div>
            )}

            {kpiLoading && (
                <>
                    {kpiCards}
                    <div className={s.bodyLoading}>
                        <Spin />
                    </div>
                </>
            )}

            {!kpiLoading && visitsOnly && (
                <>
                    {kpiCards}
                    {claimsTable}
                </>
            )}

            {!kpiLoading && waiting && !visitsOnly && (
                <>
                    {kpiCards}
                    {ehrReady ? (
                        <>
                            {showTagBanner && <TagClientsBanner isGroup={isGroup} />}
                            <WaitingCard
                                ehr={ehrState}
                                status={billingStatus}
                                isGroup={isGroup}
                                canManage={canManage}
                                claimScope={vo?.claimSettings?.claimScope}
                                summary={summary}
                                onConnectEhr={() => setSettingsTab('claim-scope')}
                                onReviewNotes={() => setTodoOpen(true)}
                                onBillMore={() => setBillMoreOpen(true)}
                                onViewAll={handleViewAll}
                            />
                        </>
                    ) : (
                        <div className={s.bodyLoading}>
                            <Spin />
                        </div>
                    )}
                </>
            )}

            {!kpiLoading && !waiting && (
                <>
                    <div className={s.tabs}>
                        <button
                            type="button"
                            className={`${s.tab} ${tab === 'claims' ? s.tabActive : ''}`}
                            onClick={() => setTab('claims')}
                        >
                            Claims
                        </button>
                        {showEraTab && (
                            <button
                                type="button"
                                className={`${s.tab} ${tab === 'era' ? s.tabActive : ''}`}
                                onClick={() => setTab('era')}
                            >
                                ERA
                            </button>
                        )}
                    </div>
                    {tab === 'claims' ? (
                        <>
                            {/* KPI 是 claims 级信息，跟着 Claims tab 走，ERA tab 天然没有 */}
                            {kpiCards}
                            {claimsTable}
                        </>
                    ) : (
                        <EraTab showProviderCol={showProviderCol} isSelfProvider={isSelfProvider} />
                    )}
                </>
            )}

            {/* waiting 态与表格态底部都展示的帮助板块：How it works / Share feedback + 支持邮箱行 */}
            {!kpiLoading && (
                <div className={s.footerHelp}>
                    <div className={s.footerBtns}>
                        <Button size="large" onClick={() => setHowItWorksOpen(true)}>
                            How it works
                        </Button>
                        <Button size="large" onClick={() => setFeedbackOpen(true)}>
                            Share feedback
                        </Button>
                    </div>
                    {/* beta 期间 claim / payment 的问题走跟计费团队约时间，而不是共享邮箱。
                        文案说的是「点了会发生什么」—— 它开的是日程工具，不是邮件草稿 */}
                    <p className={s.supportFootnote}>
                        Questions about a claim or a payment?{' '}
                        <a href={BILLING_TEAM_CALENDLY} target="_blank" rel="noopener noreferrer">
                            Book time with the Klarity billing team
                        </a>
                    </p>
                </div>
            )}

            <ClaimDrawer claimId={openClaimId} apiScope={apiScope} onClose={() => setOpenClaimId(null)} />
            <VisitDrawer
                encounterId={openVisitId}
                apiScope={apiScope}
                showProviderCol={showProviderCol}
                onClose={() => setOpenVisitId(null)}
                onClaimFound={handleVisitClaimFound}
            />
            <BillMorePatientsModal
                open={billMoreOpen}
                vo={vo}
                connections={connections}
                onClose={() => setBillMoreOpen(false)}
                onSaved={refreshAfterSettings}
            />
            <ReportDrawer weekStart={reportWeekStart} apiScope={apiScope} onClose={() => setReportWeekStart(null)} />
            <BillingInfoModal
                open={settingsTab != null}
                initialTab={settingsTab || undefined}
                onClose={() => {
                    setSettingsTab(null);
                    refreshAfterSettings();
                }}
            />
            {/* 按需挂载：内部 hooks 挂载即拉 ehr-connections/options，团队 provider（无 Add 入口）不该发这些请求 */}
            {connectEhrOpen && (
                <EhrConnectModal
                    open
                    onClose={() => {
                        setConnectEhrOpen(false);
                        refreshAfterSettings();
                    }}
                    onConnected={handleEhrConnected}
                />
            )}
            <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            {/* claim 只由 appointment 产生：billing 入口的 To-do list 过滤掉 text visit */}
            {todoOpen && <TodoListModal open appointmentOnly onClose={() => setTodoOpen(false)} />}
        </div>
    );
};

export default InsuranceBillingDashboard;
